<template>
  <table-wrapper>
    <v-data-table-server
      class="coronet-table--highlightable backup-jobs-table"
      :items="items"
      :loading="loading"
      fixed-header
      :no-data-text="$t('general.noDataAvailable')"
      :page="pagination.page + 1"
      :items-per-page="pagination.pageSize"
      :items-length="totalItems"
      @update:options="
        $emit('page-changed', {
          page: $event.page - 1,
          pageSize: $event.itemsPerPage,
        })
      "
    >
      <template #headers>
        <tr>
          <th class="w-20">{{ $t("cloudBackupAndRestore.jobsTable.headers.app") }}</th>
          <th class="w-15">{{ $t("cloudBackupAndRestore.jobsTable.headers.status") }}</th>
          <th class="w-30">{{ $t("cloudBackupAndRestore.jobsTable.headers.description") }}</th>
          <th class="w-20">{{ $t("cloudBackupAndRestore.jobsTable.headers.startTime") }}</th>
          <th class="w-20">{{ $t("cloudBackupAndRestore.jobsTable.headers.endTime") }}</th>
        </tr>
      </template>
      <template #item="{ item, index }">
        <tr :key="index" :data-testid="`backup-jobs-table-row-${index}`">
          <td :data-testid="`backup-jobs-table-app-col-row-${index}`">
            <div class="d-flex align-center ga-2">
              <img
                v-if="workloadIcons[item.connector]"
                :src="`/images/app-service-logos/${workloadIcons[item.connector]}`"
                :alt="workloadName(item.connector)"
                class="workload-icon"
              />
              <span class="subtitle2">{{ $t(`general.keepitWorkloads.${item.connector}`) }}</span>
            </div>
          </td>
          <td :data-testid="`backup-jobs-table-status-col-row-${index}`">
            <span class="body2" :class="statusClasses[item.status]">
              {{ $t(`cloudBackupAndRestore.jobsTable.statuses.${item.status}`) }}
            </span>
          </td>
          <td :data-testid="`backup-jobs-table-description-col-row-${index}`">
            {{ item.description }}
          </td>
          <td :data-testid="`backup-jobs-table-start-time-col-row-${index}`">
            {{ formatTime(item.startTime) }}
          </td>
          <td :data-testid="`backup-jobs-table-end-time-col-row-${index}`">
            {{ formatTime(item.endTime) }}
          </td>
        </tr>
      </template>
    </v-data-table-server>
  </table-wrapper>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import TableWrapper from "@/components/TableWrapper.vue";
import { getFormattedDateTime } from "@/_helpers/date";
import {
  BACKUP_JOB_STATUS_TEXT_CLASSES,
  KEEPIT_WORKLOAD_ICONS,
  type BackupJob,
  type KeepitWorkload,
} from "@/constants/cloud-backup.ts";
import type { Pagination } from "@/types";

export default defineComponent({
  components: { TableWrapper },
  props: {
    items: {
      type: Array as PropType<BackupJob[]>,
      required: true,
    },
    pagination: {
      type: Object as PropType<Pagination>,
      required: true,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["page-changed"],
  setup() {
    const i18n = useI18n();

    const workloadName = (workload: KeepitWorkload): string =>
      i18n.t(`general.keepitWorkloads.${workload}`);

    const formatTime = (time?: number): string =>
      (time ? getFormattedDateTime(time) : undefined) ?? "—";

    return {
      workloadIcons: KEEPIT_WORKLOAD_ICONS,
      statusClasses: BACKUP_JOB_STATUS_TEXT_CLASSES,
      workloadName,
      formatTime,
    };
  },
});
</script>

<style scoped lang="scss">
.v-table {
  border: 1px solid rgb(var(--v-theme-indigo-faint)) !important;
  box-shadow: none !important;
}

.workload-icon {
  width: 24px;
  height: 24px;
}
</style>
